// Invoice Export Utilities
// Creates artifact snapshots for review before submission to Finance
// No direct cross-system mutation — these are immutable exports

/**
 * Download a file to the user's browser
 * @param {string} filename - The name of the file to download
 * @param {string|Blob} content - The content to download
 * @param {string} mimeType - The MIME type of the content
 */
export function downloadBlob(filename, content, mimeType) {
  const blob = content instanceof Blob 
    ? content 
    : new Blob([content], { type: mimeType });
  
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

/**
 * Sanitize a string for use in filenames
 * @param {string} str - The string to sanitize
 * @returns {string} - Safe filename component
 */
export function sanitizeFilename(str) {
  return str
    .replace(/[^a-zA-Z0-9_-]/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
}

/**
 * Convert an invoice to CSV format
 * Format: type, date, invoice_id, order_reference, customer, platform, description, amount
 * 
 * @param {object} invoice - The invoice object to convert
 * @returns {string} - CSV string
 */
export function invoiceToCSV(invoice) {
  const rows = [];
  
  // Header row
  rows.push('type,date,invoice_id,order_reference,customer,platform,description,amount');
  
  // Meta row with invoice header info
  const orderRef = invoice.order_id || invoice.orderReference || '';
  rows.push(`meta,${invoice.date},${invoice.invoice_id},${orderRef},${quoteCSV(invoice.customer)},${invoice.platform},Invoice Header,`);
  
  // Line items
  if (invoice.line_items && invoice.line_items.length > 0) {
    invoice.line_items.forEach(item => {
      const description = item.description || item.name || '';
      const amount = (item.amount || 0).toFixed(2);
      rows.push(`line_item,${invoice.date},${invoice.invoice_id},${orderRef},${quoteCSV(invoice.customer)},${invoice.platform},${quoteCSV(description)},${amount}`);
    });
  }
  
  // Fees
  if (invoice.fees && invoice.fees.length > 0) {
    invoice.fees.forEach(fee => {
      const description = fee.type || fee.description || fee.name || '';
      const amount = (fee.amount || 0).toFixed(2);
      rows.push(`fee,${invoice.date},${invoice.invoice_id},${orderRef},${quoteCSV(invoice.customer)},${invoice.platform},${quoteCSV(description)},${amount}`);
    });
  }
  
  // Shipping
  const shippingAmount = typeof invoice.shipping === 'number' 
    ? invoice.shipping 
    : (invoice.shipping?.amount || 0);
  
  if (shippingAmount > 0) {
    const shippingDesc = invoice.shipping?.description || 'Shipping Cost';
    rows.push(`shipping,${invoice.date},${invoice.invoice_id},${orderRef},${quoteCSV(invoice.customer)},${invoice.platform},${quoteCSV(shippingDesc)},${shippingAmount.toFixed(2)}`);
  }
  
  // Notes
  if (invoice.notes) {
    rows.push(`note,${invoice.date},${invoice.invoice_id},${orderRef},${quoteCSV(invoice.customer)},${invoice.platform},${quoteCSV(invoice.notes)},`);
  }
  
  return rows.join('\n');
}

/**
 * Quote a CSV field if it contains special characters
 * @param {string} field - The field to quote
 * @returns {string} - Quoted field
 */
function quoteCSV(field) {
  if (!field) return '';
  const str = String(field);
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Convert an invoice to JSON format (prettified)
 * @param {object} invoice - The invoice object to convert
 * @returns {string} - JSON string with 2-space indentation
 */
export function invoiceToJSON(invoice) {
  // Create a clean artifact representation
  const artifact = {
    artifactType: 'SalesInvoice',
    version: '1.0',
    createdAt: invoice.created_at || new Date().toISOString(),
    status: invoice.status || 'Draft',
    invoiceId: invoice.invoice_id,
    orderReference: invoice.order_id || invoice.orderReference || '',
    invoiceDate: invoice.date,
    customer: invoice.customer,
    platform: invoice.platform,
    source: invoice.source || 'Manual',
    lineItems: (invoice.line_items || []).map(item => ({
      description: item.description || item.name || '',
      amount: parseFloat((item.amount || 0).toFixed(2))
    })),
    fees: (invoice.fees || []).map(fee => ({
      type: fee.type || fee.description || fee.name || '',
      amount: parseFloat((fee.amount || 0).toFixed(2))
    })),
    shipping: typeof invoice.shipping === 'number' 
      ? {
          description: 'Shipping Cost',
          amount: parseFloat(invoice.shipping.toFixed(2))
        }
      : {
          description: invoice.shipping?.description || 'Shipping Cost',
          amount: parseFloat((invoice.shipping?.amount || 0).toFixed(2))
        },
    notes: invoice.notes || '',
    totals: calculateTotals(invoice)
  };
  
  return JSON.stringify(artifact, null, 2);
}

/**
 * Calculate invoice totals
 * @param {object} invoice - The invoice object
 * @returns {object} - Calculated totals
 */
function calculateTotals(invoice) {
  const subtotal = (invoice.line_items || []).reduce((sum, item) => sum + (item.amount || 0), 0);
  const totalFees = (invoice.fees || []).reduce((sum, fee) => sum + (fee.amount || 0), 0);
  const shippingAmount = typeof invoice.shipping === 'number' 
    ? invoice.shipping 
    : (invoice.shipping?.amount || 0);
  const totalAmount = subtotal + totalFees + shippingAmount;
  
  return {
    subtotal: parseFloat(subtotal.toFixed(2)),
    totalFees: parseFloat(totalFees.toFixed(2)),
    shipping: parseFloat(shippingAmount.toFixed(2)),
    totalAmount: parseFloat(totalAmount.toFixed(2))
  };
}

/**
 * Generate a download filename for an invoice
 * Format: INV-{PLATFORM}-{ID}_{DATE}.{ext}
 * @param {object} invoice - The invoice object
 * @param {string} extension - File extension (json, csv, pdf)
 * @returns {string} - Safe filename
 */
export function generateInvoiceFilename(invoice, extension) {
  const platform = sanitizeFilename(invoice.platform || 'UNKNOWN');
  const invoiceId = sanitizeFilename(invoice.invoice_id || 'DRAFT');
  const date = invoice.date || new Date().toISOString().split('T')[0];
  
  return `INV-${platform}-${invoiceId}_${date}.${extension}`;
}

/**
 * Download an invoice as JSON
 * Artifacts are source-of-truth; JSON preserves structure
 * @param {object} invoice - The invoice to download
 */
export function downloadInvoiceJSON(invoice) {
  const filename = generateInvoiceFilename(invoice, 'json');
  const content = invoiceToJSON(invoice);
  downloadBlob(filename, content, 'application/json');
}

/**
 * Download an invoice as CSV
 * CSV is human-readable and import-friendly
 * @param {object} invoice - The invoice to download
 */
export function downloadInvoiceCSV(invoice) {
  const filename = generateInvoiceFilename(invoice, 'csv');
  const content = invoiceToCSV(invoice);
  downloadBlob(filename, content, 'text/csv');
}
